import React from "react";
import {
  AFRICA_MARKETS,
  DIASPORA_MARKETS,
  MARKET_GROUP_LABELS,
  SADC_MARKETS,
  buildMarketLanguageSummary,
  getMarketByIso,
} from "@/config/markets.js";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import { getLocalizedMarketName } from "@/utils/i18nGuardrails.js";
import TextField56 from "@/sot/primitives/TextField56.jsx";

function flagSrc(isoOrPath) {
  if (!isoOrPath) return "/za.svg";
  if (isoOrPath.startsWith("/")) return isoOrPath;
  return `/${String(isoOrPath).toLowerCase().replace(/[^a-z]/g, "")}.svg`;
}

function groupMarkets(query, languageCode) {
  const normalizedQuery = String(query || "").trim().toLowerCase();
  const groups = [
    {
      key: "sadc",
      label: MARKET_GROUP_LABELS.sadc,
      items: SADC_MARKETS,
    },
    {
      key: "africa",
      label: MARKET_GROUP_LABELS.africa,
      items: AFRICA_MARKETS,
    },
    {
      key: "diaspora",
      label: MARKET_GROUP_LABELS.diaspora,
      items: DIASPORA_MARKETS,
    },
  ];

  if (!normalizedQuery) return groups;

  return groups
    .map((group) => ({
      ...group,
      items: group.items.filter((market) => {
        const haystack = [
          market.name,
          getLocalizedMarketName(market, languageCode),
          market.dialCode,
          buildMarketLanguageSummary(market),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return haystack.includes(normalizedQuery);
      }),
    }))
    .filter((group) => group.items.length > 0);
}

export default function MarketCountryPicker({
  value = "",
  label,
  placeholder,
  onChange,
  compact = false,
  showFlags = true,
  showLanguageSummary = false,
  ariaLabel,
  searchPlaceholder,
  tone = "auth",
  placeholderFlagIso = "za",
  useTextFieldTrigger = false,
  disabled = false,
}) {
  const { t, languageCode } = useLanguage();
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const rootRef = React.useRef(null);
  const triggerRef = React.useRef(null);
  const selectedMarket = getMarketByIso(value);
  const placeholderFlagMarket = getMarketByIso(placeholderFlagIso);
  const groups = React.useMemo(
    () => groupMarkets(query, languageCode),
    [languageCode, query],
  );
  // The selectable country list is an elevated picker surface, even when its
  // trigger participates in a ghost form field. Keep this identical to the
  // established Address-country picker rather than letting the open list fade
  // into the dialog field background.
  const menuBackground = "rgb(var(--clr-surface-cont-highest))";
  const menuBorder = "rgba(var(--clr-outline-variant), 0.95)";
  const resolvedLabel = label ?? t("fields.country.label", "Country");
  const resolvedPlaceholder = placeholder ?? t("fields.country.placeholder", "Select country");
  const resolvedAriaLabel = ariaLabel ?? t("fields.country.placeholder", "Select country");
  const resolvedSearchPlaceholder =
    searchPlaceholder ?? t("fields.country.searchPlaceholder", "Search countries");
  const selectedLabel = t("fields.country.selected", "Selected");
  const chooseMarketLabel = t("fields.country.chooseMarket", "Choose your market");
  const currentLabel = t("fields.country.current", "Current");
  const noMarketsLabel = t("fields.country.noMatchesLong", "No markets match that search.");
  const noMatchesLabel = t("fields.country.noMatchesShort", "No matches");

  const close = React.useCallback(({ returnFocus = false } = {}) => {
    setOpen(false);
    setQuery("");
    if (returnFocus) window.requestAnimationFrame(() => triggerRef.current?.focus?.());
  }, []);

  React.useEffect(() => {
    function handlePointerDown(event) {
      if (!open) return;
      if (!rootRef.current?.contains(event.target)) close({ returnFocus: true });
    }

    function handleKeyDown(event) {
      if (open && event.key === "Escape") {
        event.preventDefault();
        event.stopImmediatePropagation();
        close({ returnFocus: true });
      }
    }

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown, true);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [close, open]);

  React.useEffect(() => {
    if (disabled) close();
  }, [close, disabled]);

  function choose(market) {
    if (disabled) return;
    onChange?.(market);
    close({ returnFocus: true });
  }

  function renderMarketButton(market) {
    const isSelected = market.iso === selectedMarket?.iso;
    const summary = showLanguageSummary ? buildMarketLanguageSummary(market) : "";
    const displayName = getLocalizedMarketName(market, languageCode);

    return (
      <button
        key={market.iso}
        type="button"
        onClick={() => choose(market)}
        className={`w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-[rgba(24,50,119,0.05)] ${
          isSelected ? "bg-[rgba(77,163,255,0.08)]" : ""
        }`}
      >
          {showFlags ? (
            <img
              alt=""
              width={18}
              height={18}
              draggable="false"
              src={flagSrc(market.flag || market.iso)}
              className="mt-0.5 h-[18px] w-[18px] shrink-0 rounded-full object-cover"
              onError={(event) => {
                event.currentTarget.onerror = null;
                event.currentTarget.src = flagSrc("za");
              }}
          />
        ) : null}
        <span className="min-w-0 flex-1">
          <span className="block text-body-medium text-on-surface">
            {displayName}
          </span>
          {summary ? (
            <span className="mt-0.5 block text-[12px] leading-[16px] text-on-surface-var">
              {summary}
            </span>
          ) : null}
        </span>
        {isSelected ? (
          <span className="pt-0.5 text-[12px] font-medium text-[rgb(var(--afrimed-primary))]">
            {selectedLabel}
          </span>
        ) : null}
      </button>
    );
  }

  if (compact) {
    const summary =
      showLanguageSummary && selectedMarket
        ? buildMarketLanguageSummary(selectedMarket)
        : "";

    return (
      <div ref={rootRef} className="relative">
        <button
          type="button"
          aria-label={ariaLabel}
          onClick={disabled ? undefined : () => setOpen((current) => !current)}
          disabled={disabled}
          className="inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm bg-[var(--afrimed-surface-2)] hover:bg-[var(--afrimed-surface-3)]"
          style={{ borderColor: "rgba(0,0,0,0.08)" }}
        >
          {showFlags && selectedMarket ? (
            <img
              src={flagSrc(selectedMarket?.flag || selectedMarket?.iso)}
              alt=""
              className="h-4 w-6 rounded-sm object-cover"
              onError={(event) => {
                event.currentTarget.onerror = null;
                event.currentTarget.src = flagSrc("za");
              }}
            />
          ) : null}
          <span className="text-[13px] text-on-surface">
            {(selectedMarket
              ? getLocalizedMarketName(selectedMarket, languageCode)
              : "") || resolvedPlaceholder}
          </span>
          <img
            src="/icons/pharmacy/drop-down-chevron.svg"
            alt=""
            aria-hidden="true"
            className="h-[14px] w-[14px] shrink-0"
          />
        </button>

        {open ? (
          <div
            className="absolute right-0 top-[calc(100%+8px)] z-50 w-[min(28rem,calc(100vw-2rem))] rounded-[16px] border bg-white shadow-[0_18px_48px_rgba(24,50,119,0.18)]"
            style={{ borderColor: "rgba(24,50,119,0.12)" }}
          >
            <div className="border-b px-4 py-3" style={{ borderColor: "rgba(24,50,119,0.08)" }}>
                  <div className="text-label-medium text-on-surface">{chooseMarketLabel}</div>
                  {summary ? (
                    <div className="mt-1 text-[12px] leading-[16px] text-on-surface-var">
                      {currentLabel}: {getLocalizedMarketName(selectedMarket, languageCode)} · {summary}
                    </div>
                  ) : null}
                </div>

            <div className="p-3">
              <input
                autoFocus
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={resolvedSearchPlaceholder}
                className="h-10 w-full rounded-[10px] border px-3 outline-none text-body-medium"
                style={{ borderColor: "rgba(24,50,119,0.14)" }}
              />
            </div>

            <div className="max-h-[22rem] overflow-auto pb-2">
              {groups.map((group) => (
                <div key={group.key}>
                  <div className="px-4 py-2 text-label-medium text-on-surface-var bg-[rgba(24,50,119,0.04)]">
                    {group.label}
                  </div>
                  {group.items.map(renderMarketButton)}
                </div>
              ))}
              {groups.length === 0 ? (
                <div className="px-4 py-6 text-body-medium text-on-surface-var">
                  {noMarketsLabel}
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  const summary =
    showLanguageSummary && selectedMarket
      ? buildMarketLanguageSummary(selectedMarket)
      : "";

  return (
    <div ref={rootRef} className="relative w-full">
      {useTextFieldTrigger ? (
        <TextField56
          label={resolvedLabel}
          value={selectedMarket ? getLocalizedMarketName(selectedMarket, languageCode) : ""}
          placeholder={resolvedPlaceholder}
          readOnly
          tone={tone}
          leadingSlot={showFlags && (selectedMarket || placeholderFlagMarket) ? <img
            src={flagSrc(selectedMarket?.flag || selectedMarket?.iso || placeholderFlagMarket?.flag || placeholderFlagMarket?.iso)}
            alt=""
            className="h-5 w-5 rounded-full object-cover"
            onError={(event) => {
              event.currentTarget.onerror = null;
              event.currentTarget.src = flagSrc("za");
            }}
          /> : null}
          trailingSlotFullHeight
          trailingSlot={<button
            ref={triggerRef}
            type="button"
            aria-label={resolvedAriaLabel}
            aria-haspopup="listbox"
            aria-expanded={open}
            onClick={disabled ? undefined : () => setOpen((current) => !current)}
            disabled={disabled}
            className="grid min-h-full w-12 place-items-center rounded-r-[12px] text-on-surface-var outline-none transition-colors hover:bg-[rgba(var(--clr-on-surface),0.06)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[rgb(var(--afrimed-primary))]"
          >
            <img
              src="/icons/pharmacy/drop-down-chevron.svg"
              alt=""
              aria-hidden="true"
              className="h-4 w-4 shrink-0"
            />
          </button>}
        />
      ) : resolvedLabel ? (
        <div className="px-4 pt-3 pb-1 text-label-medium text-on-surface-var">
          {resolvedLabel}
        </div>
      ) : null}

      {!useTextFieldTrigger ? <div
        className="h-14 rounded-[12px] border flex items-center px-4"
        style={{
          background: tone === "ghost" ? "rgb(var(--clr-surface-container-low))" : tone === "surface" ? "rgb(var(--clr-surface-cont-highest))" : "rgb(var(--afrimed-auth-input-surface))",
          borderColor: tone === "surface" || tone === "ghost" ? "rgba(var(--clr-outline-variant), 0.95)" : "rgba(24,50,119,0.16)",
        }}
      >
        <button
          type="button"
          aria-label={resolvedAriaLabel}
          onClick={disabled ? undefined : () => setOpen((current) => !current)}
          disabled={disabled}
          className="flex w-full items-center gap-3 text-left"
        >
          {showFlags && (selectedMarket || placeholderFlagMarket) ? (
            <img
              src={flagSrc(
                selectedMarket?.flag ||
                  selectedMarket?.iso ||
                  placeholderFlagMarket?.flag ||
                  placeholderFlagMarket?.iso
              )}
              alt=""
              className="h-5 w-5 shrink-0 rounded-full object-cover"
              onError={(event) => {
                event.currentTarget.onerror = null;
                event.currentTarget.src = flagSrc("za");
              }}
            />
          ) : null}
          <span className="min-w-0 flex-1">
            <span
              className={`block truncate text-body-medium ${
                selectedMarket ? "text-on-surface" : "text-on-surface-var"
              }`}
            >
              {selectedMarket?.name || resolvedPlaceholder}
            </span>
            {summary ? (
              <span className="mt-0.5 block truncate text-[12px] leading-[16px] text-on-surface-var">
                {summary}
              </span>
            ) : null}
          </span>
          <img
            src="/icons/pharmacy/drop-down-chevron.svg"
            alt=""
            aria-hidden="true"
            className="h-4 w-4 shrink-0"
          />
        </button>
      </div> : null}

      {open ? (
        <div
          role="listbox"
          aria-label={chooseMarketLabel}
          className="absolute left-0 right-0 top-[calc(100%+8px)] z-50 rounded-[12px] border shadow-[0_12px_32px_rgba(24,50,119,0.15)]"
          style={{ background: menuBackground, borderColor: menuBorder }}
        >
          <div className="p-2">
            <input
              autoFocus
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={resolvedSearchPlaceholder}
              className="h-9 w-full rounded-[8px] border px-3 outline-none text-on-surface placeholder:text-on-surface-var"
              style={{ background: menuBackground, borderColor: menuBorder }}
            />
          </div>

          <div className="max-h-64 overflow-auto py-2">
            {groups.map((group) => (
              <div key={group.key}>
                <div className="px-3 py-2 text-label-medium text-on-surface-var bg-[rgba(24,50,119,0.04)]">
                  {group.label}
                </div>
                {group.items.map(renderMarketButton)}
              </div>
            ))}
            {groups.length === 0 ? (
              <div className="px-3 py-3 text-on-surface-var">{noMatchesLabel}</div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
